import * as React from 'react';

import { Col, Divider, Empty, Form, Input, Row, Select } from 'antd';

import { DecimalInputNumber } from 'components';
import { useProductValue } from 'context';
import type {
  AttributeConfigType,
  BaseAttribute,
  CategorySummary,
} from 'types';
import { cn, inputRule, randomId } from 'utils';

type FieldType = Partial<Record<string, string | number | string[]>>;

type EnhancedAttribute = BaseAttribute &
  Record<'relatedCategories', CategorySummary[]>;

type GroupedAttribute = {
  type: AttributeConfigType;
  data: EnhancedAttribute[];
};

const ATTRIBUTE_SORT_ORDER: AttributeConfigType[] = [
  'Text',
  'Number',
  'Select',
  'CheckBox',
];

const ProductAttributesInfo = () => {
  const {
    mainCategory,
    otherCategories = [],
    baseAttributes = [],
  } = useProductValue();

  const formattedAttributes = React.useMemo<GroupedAttribute[]>(() => {
    const groupedAttributes = baseAttributes.reduce<
      Partial<Record<AttributeConfigType, GroupedAttribute>>
    >((previousValue, currentValue) => {
      const { id, type } = currentValue;

      const relatedCategories = otherCategories.filter(
        ({ relatedAttribute }) => {
          const { id: relatedAttributeId } = relatedAttribute ?? {};
          return relatedAttributeId === id;
        },
      );
      const enhancedAttribute: EnhancedAttribute = {
        ...currentValue,
        relatedCategories,
      };

      if (previousValue[type]) {
        const { data = [] } = previousValue[type] ?? {};
        data.push(enhancedAttribute);
      } else {
        previousValue[type] = { type, data: [enhancedAttribute] };
      }

      return previousValue;
    }, {});
    // Sort groups based on ATTRIBUTE_SORT_ORDER
    const sortedAttributes = Object.values(groupedAttributes).sort((a, b) => {
      const indexA = ATTRIBUTE_SORT_ORDER.indexOf(a.type);
      const indexB = ATTRIBUTE_SORT_ORDER.indexOf(b.type);

      // Handle types not in the sort order (put them at the end)
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return sortedAttributes;
  }, [baseAttributes, otherCategories]);

  const showEmptyAttributes = baseAttributes.length === 0;

  return (
    <Row>
      {!showEmptyAttributes ? (
        formattedAttributes.map(({ type, data }, index) => {
          const groupId = randomId();
          const isInitialGroup = index === 0;
          const isTextGroup = type === 'Text';
          const isNumberGroup = type === 'Number';
          const isCheckBoxGroup = type === 'CheckBox';
          const isSelectGroup = type === 'Select';

          return (
            <React.Fragment key={groupId}>
              {!isInitialGroup ? <Divider dashed className='mt-4' /> : null}
              {data.map(({ id, name, values, relatedCategories }, index) => {
                const isOddItem = index % 2 === 1;

                return (
                  <Col
                    key={id}
                    span={24}
                    lg={12}
                    className={cn(isOddItem ? 'lg:ps-3' : 'lg:pe-3')}
                  >
                    <Form.Item<FieldType>
                      label={name}
                      name={`base-attribute-${id}`}
                      rules={
                        isTextGroup
                          ? [inputRule.string]
                          : isNumberGroup
                            ? [inputRule.digit]
                            : undefined
                      }
                    >
                      {isTextGroup ? (
                        <Input placeholder={`${name} محصول را وارد کنید`} />
                      ) : isNumberGroup ? (
                        <DecimalInputNumber
                          placeholder={`${name} محصول را وارد کنید`}
                        />
                      ) : isCheckBoxGroup || isSelectGroup ? (
                        <Select
                          showSearch
                          allowClear={isSelectGroup}
                          mode={isCheckBoxGroup ? 'multiple' : undefined}
                          placeholder={`${name} محصول را انتخاب کنید`}
                          disabled={
                            isSelectGroup &&
                            relatedCategories.some(({ relatedAttribute }) => {
                              const { selectedValues = [] } =
                                relatedAttribute ?? {};
                              return selectedValues.length > 0;
                            })
                          }
                          options={values.map(value => ({
                            label: value,
                            value,
                            disabled: relatedCategories.some(
                              ({ relatedAttribute }) => {
                                const { selectedValues = [] } =
                                  relatedAttribute ?? {};
                                return selectedValues.includes(value);
                              },
                            ),
                          }))}
                        />
                      ) : (
                        <Input
                          disabled
                          placeholder={`نوع فیلد ${name} نامشخص است!`}
                        />
                      )}
                    </Form.Item>
                  </Col>
                );
              })}
            </React.Fragment>
          );
        })
      ) : (
        <Col span={24}>
          <Empty
            className='my-20'
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className='text-zinc-300'>
                {mainCategory
                  ? `دسته بندی ${mainCategory.title} فیلد های متغیر ندارد.`
                  : 'ابتدا دسته‌بندی اصلی را انتخاب کنید.'}
              </span>
            }
          />
        </Col>
      )}
    </Row>
  );
};

export { ProductAttributesInfo };
